import React, { useEffect, useState,useRef} from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

export default function SalesByPaymentMethod() {
  const { user ,companyId} = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [companySnap, setCompanySnap] = useState({});
  const perPage = 10;
  const chartRef = useRef();
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };

const loadPlan = async () => {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
  }
};

  useEffect(() => {
    loadPlan();
    // Set today's date as default for both from and to date
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const loadOrders = async () => {
    if (!user || !companyId) return;
    try {
      const q = query(
        collection(db, 'companies', companyId, 'orders'),
        where('paymentStatus', '!=', 'Void'),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadCompany = async () => {
    if (!user || !companyId) return;
    const snap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
    if (snap.exists()) setCompanySnap(snap.data());
  };

  const filtered = orders.filter((order) => {
    const orderDate = parseTimestamp(order.timestamp);
    if (!orderDate) return false;
    const from = fromDate ? new Date(fromDate) : new Date(0);
    const to = toDate ? new Date(toDate) : new Date();
    
    // Set to to end of day
    to.setHours(23, 59, 59, 999);

    const matchesSearch = search ? order.paymentMethod?.toLowerCase().includes(search.toLowerCase()) : true;
    const matchesDate = orderDate >= from && orderDate <= to;
    return matchesSearch && matchesDate;
  });

  const grouped = filtered.reduce((acc, order) => {
    const method =
      (order.paymentProvider || '').toLowerCase() === 'stripe'
        ? 'Stripe'
        : order.paymentMethod || 'Unknown';
    if (!acc[method]) acc[method] = { count: 0, total: 0 };
    acc[method].count++;
    acc[method].total += toNumber(order.total ?? order.totals?.total ?? 0);
    return acc;
  }, {});

  const paymentData = Object.entries(grouped).map(([method, data]) => ({ method, total: data.total }));
  const paginated = paymentData.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.max(1, Math.ceil(paymentData.length / perPage));
  const totalSales = paymentData.reduce((sum, item) => sum + toNumber(item.total), 0);

  const chartData = {
    labels: paymentData.map(p => p.method),
    datasets: [{
      data: paymentData.map(p => toNumber(p.total)),
      backgroundColor: ['#68e900', '#ee1546', '#00e1d7','#ff9800']
    }]
  };
  
  const chartOptions = {
    plugins: {
      title: {
        display: true,
        text: 'Payment Method',
        color: 'black',
        font: {
          size: 16
        }
      },
      datalabels: {
        color: '#fff',
        formatter: (value, context) => {
          const sum = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
          const percentage = ((value / sum) * 100).toFixed(1) + '%';
          return percentage;
        }
      },
      legend: {
        position: 'bottom',
        labels: {
          color: 'grey'
        }
      }
    }
  };

  const exportToPDF = async () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }
  
    const doc = new jsPDF();
    const title = `Sales By Payment Method Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;
  
    // Header
    doc.setFontSize(12);
    doc.text(companySnap.companyName || 'Company', 105, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.text(title, 105, 22, { align: 'center' });
  
    // Pie Chart Image
    let chartHeight = 0;
    if (chartRef.current) {
      const chartCanvas = chartRef.current.canvas;
      const chartImg = chartCanvas.toDataURL('image/png', 1.0);
      doc.addImage(chartImg, 'PNG', 40, 30, 130, 80);
      chartHeight = 80;
    }
  
    // Total Sales text
    const totalTextY = 30 + chartHeight + 10;
    doc.setFontSize(10);
    doc.text(`Total Sales: RM${totalSales.toFixed(2)}`, 105, totalTextY, { align: 'center' });
  
    // Table
    autoTable(doc, {
      head: [['Payment Method', 'Total Sales']],
      body: paymentData.map(p => [p.method, `RM${p.total.toFixed(2)}`]),
      startY: totalTextY + 10,
      styles: { fontSize: 9 },
    });
  
    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(9);
    doc.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });
  
    const file = `sales-by-payment_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`.replace(/\//g, '-');
    doc.save(file);
  };

    useEffect(() => {
      const loadAll = async () => {
        setLoading(true);
        await Promise.all([loadOrders(), loadCompany()]);
        setLoading(false);
      };
      loadAll();
    }, [user,companyId]);
  
    if (loading) {
      return (
        <div className="w-full flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
          <span className="text-white">Loading information...</span>
        </div>
      );
    }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Sales by Payment Method</h2>

      <div className="flex gap-4 mb-6">
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <span className="text-gray-400">to</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" placeholder="Search by method" />
        <button onClick={exportToPDF} className="bg-purple-600 px-4 py-2 rounded">Export to PDF</button>
      </div>

      <div className="rounded-lg p-4 mb-4 w-full max-w-md mx-auto text-white">
      <Pie ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
</div>
<div className="text-center text-lg font-semibold text-white mb-6">
  Total Sales: <span className="text-green-400">RM {totalSales.toFixed(2)}</span>
</div>
      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="px-4 py-2">Payment Method</th>
              <th className="px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((item, i) => (
              <tr key={i} className="border-t border-gray-700">
                <td className="px-4 py-2">{item.method}</td>
                <td className="px-4 py-2 font-bold">RM {item.total.toFixed(2)}</td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-gray-400" colSpan={2}>No data found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center flex-wrap mt-6 gap-2">
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`px-4 py-2 rounded ${currentPage === i + 1 ? 'bg-purple-600 text-white' : 'bg-[#2c2c2c] text-gray-300'}`}
            onClick={() => setCurrentPage(i + 1)}
          >{i + 1}</button>
        ))}
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}
